import { useState } from 'react';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';
import { Subscriptions, NewsletterSessionManager } from 'lib/services/newsletter-signup-service';
import { UpdateUserResponse } from 'lib/services/user-service';
import { UnsubscribeResponse } from 'lib/services/newsletter-unsubscribe-service';
import { CONSENT_STATUS } from 'lib/services/registration-service';

export const useEmailSubscription = (newsLetterSubscriptionId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { data: session, update: updateSession } = useSessionSafe();

  const accessToken = session?.user?.accessToken;
  const userEmail = session?.user?.email;

  const handleUpdateSession = async (
    accessToken: string,
    updatedSubscriptions: Subscriptions[]
  ) => {
    if (accessToken && updateSession && updatedSubscriptions) {
      try {
        await updateSession({
          user: {
            accessToken: accessToken,
            subscriptions: updatedSubscriptions,
          },
        });
        setSuccess(true);
        setIsLoading(false);
      } catch (sessionError) {
        console.error('[EmailSubscription] Failed to update session:', sessionError);
        setSuccess(false);
        setIsLoading(false);
      }
    }
  };

  const handleToggleSubscription = async (isSubscribedNewsLetter: boolean) => {
    if (!accessToken || !userEmail) {
      setError('Missing required information. Please try again.');
      return { success: false };
    }

    setIsLoading(true);
    setError('');
    try {
      if (isSubscribedNewsLetter) {
        // If already subscribed, suppress the email to unsubscribe
        const suppressResponse = await fetch('/api/suppress-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            source: 'Email',
          }),
        });
        const suppressResult: UnsubscribeResponse = await suppressResponse.json();
        if (!suppressResponse.ok || suppressResult.error) {
          throw new Error(
            suppressResult.error_description ||
              suppressResult.error ||
              'Failed to update subscription'
          );
        }

        // Remove the subscription from session
        const updatedSubscriptions =
          session?.user?.subscriptions?.filter(
            (subscription) => subscription.SubscriptionId !== newsLetterSubscriptionId
          ) ?? [];

        await handleUpdateSession(accessToken, updatedSubscriptions);

        // Clear the session storage flag so the newsletter component shows again
        if (newsLetterSubscriptionId) {
          NewsletterSessionManager.removeSignupCompleted(newsLetterSubscriptionId);
        }
      } else {
        const response = await fetch(`/api/auth/user-detail`, {
          method: 'PUT',
          body: JSON.stringify({
            email: userEmail,
            Subscriptions: [
              {
                SubscriptionId: newsLetterSubscriptionId,
                Subscribed: !isSubscribedNewsLetter,
              },
            ],
            Consent: {
              PrivacyPolicyAcceptance: CONSENT_STATUS.PRIVACY_POLICY,
              EmailConsent: CONSENT_STATUS.EMAIL,
              DataSharingConsent: CONSENT_STATUS.DATA_SHARING,
            },
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const responseData: UpdateUserResponse = await response.json();
        if (!response.ok || responseData.error) {
          throw new Error(
            responseData.error_description || responseData.error || 'Failed to update subscription'
          );
        }

        // If not subscribed, create new subscription and merge with existing subscriptions
        const newSubscription: Subscriptions = {
          SubscriptionId: newsLetterSubscriptionId as string,
          Subscribed: true,
        };
        const updatedSubscriptions = [...(session?.user?.subscriptions ?? []), newSubscription];

        await handleUpdateSession(responseData?.accessToken as string, updatedSubscriptions);
      }
      return { success: true };
    } catch (err) {
      console.error('Error updating subscription:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      setSuccess(false);
      setIsLoading(false);
      return { success: false };
    }
  };

  return { isLoading, error, success, handleToggleSubscription };
};
